/**
 * 투자계약 조건 계산 (#70) — 상환 · 전환 가능일, 오늘 상환하면 얼마, 계약 종료 여부.
 * 서버의 종료 판단은 backend/src/modules/compliance/rules.ts `termsEnded` — 고칠 때 같이 본다.
 */

export interface DealTerms {
  id: number; dealId: number; effectiveOn: string | null; durationYears: number | null;
  dividendParticipating: boolean | null; dividendCumulative: boolean | null; dividendRate: number | null;
  redemptionAfterYears: number | null; redemptionRate: number | null;
  conversionRatio: number | null; conversionAfterYears: number | null; conversionPrice: number | null;
  refixing: string; consultItems: string; breachLimit: number | null;
  putRate: number | null; penaltyPct: number | null; lateRate: number | null;
  annualReportDays: number | null; terminateBelowPct: number | null; terminateOnIpo: boolean;
  documentId: number | null; note: string;
}
export interface DealBreach { id: number; dealId: number; occurredOn: string; item: string; note: string }

const at = (s: string) => new Date(`${s}T00:00:00Z`);
const ymd = (d: Date) => d.toISOString().slice(0, 10);

/** 년(0.5 단위 가능)을 더한다 — 말일은 말일로 */
export function addYears(s: string, years: number) {
  const d = at(s);
  const m = d.getUTCMonth() + Math.round(years * 12);
  const last = new Date(Date.UTC(d.getUTCFullYear(), m + 1, 0)).getUTCDate();
  return ymd(new Date(Date.UTC(d.getUTCFullYear(), m, Math.min(d.getUTCDate(), last))));
}

/** 상환 · 전환 기산일 — 계약의 효력발생일, 없으면 납입일 · 투자일 */
export const baseDate = (t: Pick<DealTerms, 'effectiveOn'>, deal: { paidOn?: string | null; investedOn?: string | null }) =>
  t.effectiveOn ?? deal.paidOn ?? deal.investedOn ?? null;

/**
 * 오늘 상환하면 받을 금액 — 투자 원금 × (1 + 이율)^(경과일/365) − 받은 배당.
 * 계약마다 배당 차감 · 일할 방식이 다를 수 있어 「참고」로만 보인다.
 */
export function redemptionAmount(principal: number, ratePct: number, from: string, on: string, dividends = 0) {
  const days = Math.max(0, (at(on).getTime() - at(from).getTime()) / 86_400_000);
  return Math.round(principal * Math.pow(1 + ratePct / 100, days / 365) - dividends);
}

/** 계약의 협의권 · 보고 조항이 끝났나 — 이유 또는 null. 주식수를 모르면 끝나지 않은 것으로 본다 */
export function termsEnded(t: Pick<DealTerms, 'terminateOnIpo' | 'terminateBelowPct'>, listed: boolean, stakePct: number | null): string | null {
  if (t.terminateOnIpo && listed) return '상장';
  if (t.terminateBelowPct !== null && stakePct !== null && stakePct < t.terminateBelowPct) {
    return `지분 ${stakePct.toFixed(2)}% — 계약상 ${t.terminateBelowPct}% 미만이면 협의권 · 보고 조항이 끝난다`;
  }
  return null;
}

/** 줄마다 하나 — 빈 줄은 뺀다 */
export const lines = (s: string) => s.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
